import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Validation helpers
const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/[^\d+]/g, '');
  const phoneRegex = /^\+?\d{10,15}$/;
  return phoneRegex.test(cleaned);
};

const formatPhoneInput = (value) => {
  let cleaned = value.replace(/[^\d+]/g, '');
  if (cleaned.indexOf('+') > 0) {
    cleaned = cleaned.replace(/\+/g, '');
  }
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  return cleaned;
};

const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

const formatNumberInput = (value) => {
  return value.replace(/[^\d]/g, '');
};

// Helper to construct correct file URL
const getFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith('https://') && (fileUrl.includes('.s3.') || fileUrl.includes('amazonaws.com'))) {
    return fileUrl;
  }
  let filePath = fileUrl;
  if (filePath.startsWith('http')) {
    try {
      const url = new URL(filePath);
      filePath = url.pathname;
    } catch (e) {
      filePath = '/' + filePath.split('/').slice(3).join('/');
    }
  }
  if (filePath.startsWith('/uploads')) {
    filePath = filePath.replace('/uploads', '/api/uploads');
  } else if (!filePath.startsWith('/api/uploads')) {
    filePath = '/api/uploads/' + filePath.split('/').pop();
  }
  return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
};

export default function DynamicDealForm({ 
  formData, 
  setFormData, 
  errors = {}, 
  setErrors,
  isAdmin = false,
  onBrandNameChange // callback for duplicate name check
}) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFields, setUploadingFields] = useState({});

  const fetchFormConfig = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint;
      if (isAdmin) {
        const userEmail = localStorage.getItem('user_email');
        endpoint = `${API}/admin/form-configurations/startup?requester_email=${userEmail}`;
      } else {
        endpoint = `${API}/form-configurations/startup/public`;
      }
      const response = await axios.get(endpoint);
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching form config:', error);
      // Fallback to empty fields - form will still work with defaults
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchFormConfig();
  }, [fetchFormConfig]);

  const handleInputChange = (fieldKey, value) => {
    setFormData({ ...formData, [fieldKey]: value });
    if (errors[fieldKey]) {
      setErrors({ ...errors, [fieldKey]: null });
    }
    // Special handling for brand_name duplicate check
    if (fieldKey === 'brand_name' && onBrandNameChange) {
      onBrandNameChange(value);
    }
  };

  const handleFileUpload = async (fieldKey, file, uploadEndpoint) => {
    if (!file) return;

    setUploadingFields({ ...uploadingFields, [fieldKey]: true });
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Use specific endpoint for deck uploads
      const endpoint = uploadEndpoint || `${API}/upload-file?folder=uploads`;
      const response = await axios.post(endpoint, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleInputChange(fieldKey, response.data.file_url);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFields({ ...uploadingFields, [fieldKey]: false });
    }
  };

  const handleRemoveFile = (fieldKey) => {
    handleInputChange(fieldKey, '');
  };

  // Founders field handlers
  const handleFounderChange = (index, field, value) => {
    const founders = [...(formData.founders || [{ name: '', email: '', phone: '', linkedin: '' }])];
    founders[index] = { ...founders[index], [field]: value };
    setFormData({ ...formData, founders });
  };

  const addFounder = () => {
    const founders = [...(formData.founders || []), { name: '', email: '', phone: '', linkedin: '' }];
    setFormData({ ...formData, founders });
  };

  const removeFounder = (index) => {
    if ((formData.founders || []).length <= 1) return;
    const founders = (formData.founders || []).filter((_, i) => i !== index);
    setFormData({ ...formData, founders });
  };

  // Previous fundraises handlers
  const handleFundraiseChange = (index, field, value) => {
    const fundraises = [...(formData.previous_fundraises || [])];
    fundraises[index] = { ...fundraises[index], [field]: value };
    setFormData({ ...formData, previous_fundraises: fundraises });
  };

  const addFundraise = () => {
    const fundraises = [...(formData.previous_fundraises || []), { type: 'Fundraise', valuation: '', grant_name: '', amount: '' }];
    setFormData({ ...formData, previous_fundraises: fundraises });
  };

  const removeFundraise = (index) => {
    const fundraises = (formData.previous_fundraises || []).filter((_, i) => i !== index);
    setFormData({ ...formData, previous_fundraises: fundraises });
  };

  // Render a single field based on its type
  const renderField = (field) => {
    const value = formData[field.field_key] || '';
    const error = errors[field.field_key];
    const isUploading = uploadingFields[field.field_key];

    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className={`bg-white ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
              maxLength={field.validation?.maxLength}
            />
            {field.validation?.maxLength && (
              <p className="text-xs text-gray-500">{value.length}/{field.validation.maxLength}</p>
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className={`bg-white min-h-[80px] ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'email':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="email"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder || 'email@example.com'}
              className={`bg-white ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'phone':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="tel"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, formatPhoneInput(e.target.value))}
              placeholder={field.placeholder || '+91 9876543210'}
              className={`bg-white ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'url':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="url"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder || 'https://example.com'}
              className={`bg-white ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, formatNumberInput(e.target.value))}
              placeholder={field.placeholder || 'Enter number'}
              className={`bg-white ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select value={value} onValueChange={(val) => handleInputChange(field.field_key, val)}>
              <SelectTrigger className={`bg-white ${error ? 'border-red-500 border-2' : 'border-[#E2E8F0]'}`}>
                <SelectValue placeholder={field.placeholder || 'Select option'} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(field.options || []).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className={`flex flex-wrap gap-2 p-2 border rounded-md bg-white min-h-[42px] ${error ? 'border-red-500 border-2' : ''}`}>
              {(field.options || []).map((opt) => {
                const isSelected = selectedValues.includes(opt.value);
                return (
                  <Badge
                    key={opt.value}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`cursor-pointer ${isSelected ? 'bg-brand' : ''}`}
                    onClick={() => {
                      const newValue = isSelected
                        ? selectedValues.filter(v => v !== opt.value)
                        : [...selectedValues, opt.value];
                      handleInputChange(field.field_key, newValue);
                    }}
                  >
                    {opt.label}
                  </Badge>
                );
              })}
            </div>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="date"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              className={`bg-white ${error ? 'border-red-500 border-2 focus:ring-red-500 focus:border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'file':
        const uploadEndpoint = field.field_key === 'deck_url' 
          ? (isAdmin ? `${API}/admin/upload-deck` : `${API}/startup-applications/upload-deck`)
          : `${API}/upload-file?folder=uploads`;
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all
              ${isUploading ? 'border-gray-400 bg-gray-50' : 'border-gray-300 hover:border-brand'}
              ${value ? 'border-green-500 bg-green-50' : ''}
              ${error ? '!border-red-500 bg-red-50' : ''}
            `}>
              {isUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-brand" />
                  <span className="text-sm text-gray-500">Uploading...</span>
                </div>
              ) : value ? (
                <div className="space-y-1">
                  <div className="text-green-600 text-sm">✓ File uploaded</div>
                  <div className="flex items-center justify-center gap-2 relative z-10">
                    <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                      View file →
                    </a>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveFile(field.field_key); }}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Click to replace</p>
                </div>
              ) : (
                <div>
                  <Upload className="mx-auto h-6 w-6 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">{field.placeholder || 'Click to upload'}</p>
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx,.png,.jpg,.jpeg"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => handleFileUpload(field.field_key, e.target.files[0], uploadEndpoint)}
                disabled={isUploading}
              />
            </div>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'founders':
        return (
          <div key={field.id} className="space-y-4 col-span-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Button type="button" onClick={addFounder} variant="outline" size="sm" className="border-brand text-brand">
                <Plus className="h-4 w-4 mr-1" /> Add Co-founder
              </Button>
            </div>
            {(formData.founders || [{ name: '', email: '', phone: '', linkedin: '' }]).map((founder, idx) => {
              // Check for individual founder field errors
              const founderEmailError = errors[`founder_${idx}_email`];
              const founderPhoneError = errors[`founder_${idx}_phone`];
              const founderLinkedinError = errors[`founder_${idx}_linkedin`];
              
              return (
                <div key={idx} className={`p-3 border rounded-lg bg-gray-50 space-y-3 ${error ? 'border-red-500 border-2' : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      {idx === 0 ? 'Founder' : `Co-founder ${idx}`}
                    </span>
                    {idx > 0 && (
                      <Button type="button" onClick={() => removeFounder(idx)} variant="ghost" size="sm" className="text-red-500 h-6 w-6 p-0">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={founder.name || ''}
                        onChange={(e) => handleFounderChange(idx, 'name', e.target.value)}
                        placeholder="Full Name"
                        className="bg-white h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input
                        value={founder.email || ''}
                        onChange={(e) => handleFounderChange(idx, 'email', e.target.value)}
                        placeholder="Email"
                        className={`bg-white h-9 text-sm ${founderEmailError ? 'border-red-500 border-2' : ''}`}
                      />
                      {founderEmailError && <p className="text-red-500 text-xs">{founderEmailError}</p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Phone</Label>
                      <Input
                        value={founder.phone || ''}
                        onChange={(e) => handleFounderChange(idx, 'phone', formatPhoneInput(e.target.value))}
                        placeholder="Phone (+919876543210)"
                        className={`bg-white h-9 text-sm ${founderPhoneError ? 'border-red-500 border-2' : ''}`}
                      />
                      {founderPhoneError && <p className="text-red-500 text-xs">{founderPhoneError}</p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">LinkedIn</Label>
                      <Input
                        value={founder.linkedin || ''}
                        onChange={(e) => handleFounderChange(idx, 'linkedin', e.target.value)}
                        placeholder="LinkedIn URL"
                        className={`bg-white h-9 text-sm ${founderLinkedinError ? 'border-red-500 border-2' : ''}`}
                      />
                      {founderLinkedinError && <p className="text-red-500 text-xs">{founderLinkedinError}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'previous_fundraises':
        return (
          <div key={field.id} className="space-y-4 col-span-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">
                {field.label}
                <span className="text-gray-500 font-normal ml-1">(Optional)</span>
              </Label>
              <Button type="button" onClick={addFundraise} variant="outline" size="sm" className="border-brand text-brand">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            {(formData.previous_fundraises || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic">No previous fundraises added.</p>
            ) : (
              (formData.previous_fundraises || []).map((fundraise, idx) => (
                <div key={idx} className="p-3 border rounded-lg bg-gray-50 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">Fundraise {idx + 1}</span>
                    <Button type="button" onClick={() => removeFundraise(idx)} variant="ghost" size="sm" className="text-red-500 h-6 w-6 p-0">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={fundraise.type || 'Fundraise'} onValueChange={(val) => handleFundraiseChange(idx, 'type', val)}>
                      <SelectTrigger className="bg-white h-9">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Fundraise">Fundraise</SelectItem>
                        <SelectItem value="Grant">Grant</SelectItem>
                      </SelectContent>
                    </Select>
                    {fundraise.type === 'Fundraise' ? (
                      <Input
                        value={fundraise.valuation || ''}
                        onChange={(e) => handleFundraiseChange(idx, 'valuation', formatNumberInput(e.target.value))}
                        placeholder="Valuation (INR)"
                        className="bg-white h-9"
                      />
                    ) : (
                      <Input
                        value={fundraise.grant_name || ''}
                        onChange={(e) => handleFundraiseChange(idx, 'grant_name', e.target.value)}
                        placeholder="Grant Name"
                        className="bg-white h-9"
                      />
                    )}
                    <Input
                      value={fundraise.amount || ''}
                      onChange={(e) => handleFundraiseChange(idx, 'amount', formatNumberInput(e.target.value))}
                      placeholder="Amount Raised (INR)"
                      className="bg-white h-9 col-span-2"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        );

      default:
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">{field.label}</Label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className="bg-white"
            />
          </div>
        );
    }
  };

  // Group fields by section
  const groupedFields = fields.reduce((acc, field) => {
    const section = field.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
        <span className="ml-2 text-gray-500">Loading form...</span>
      </div>
    );
  }

  // If no fields loaded, show nothing (fallback to existing form)
  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedFields).map(([section, sectionFields]) => (
        <div key={section} className="space-y-4 border-b border-[#E2E8F0] pb-4 last:border-b-0">
          <h3 className="text-md font-semibold text-[#0F172A]">{section}</h3>
          <div className="grid grid-cols-2 gap-4">
            {sectionFields
              .sort((a, b) => a.order - b.order)
              .map((field) => renderField(field))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Export validation function for use in parent components
export const validateDynamicDealForm = async (formType = 'startup') => {
  try {
    const userEmail = localStorage.getItem('user_email');
    const endpoint = userEmail 
      ? `${API}/admin/form-configurations/${formType}?requester_email=${userEmail}`
      : `${API}/form-configurations/${formType}/public`;
    const response = await axios.get(endpoint);
    return response.data.fields || [];
  } catch {
    return [];
  }
};
